import { useState } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import BrandPanel from "../components/BrandPanel";
import LangToggle from "../components/LangToggle";
import { ShieldCheck, Smartphone, Key, ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * TOTP challenge screen — shown after Login when the user's account has
 * 2FA enabled. Accepts either a fresh 6-digit code from Google Authenticator
 * or one of the unused backup codes.
 *
 * Navigation arg (via location.state):
 *   { login_token: string, company_name: string }
 */
export default function TotpChallenge() {
    const navigate = useNavigate();
    const location = useLocation();
    const { loginTotp } = useAuth();
    const { lang, t, toggle } = useLang();

    const loginToken = location.state?.login_token;
    const companyName = location.state?.company_name || "";

    const [mode, setMode] = useState("totp"); // "totp" | "backup"
    const [code, setCode] = useState("");
    const [backupCode, setBackupCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // No token in state → redirect back to login.
    if (!loginToken) return <Navigate to="/login" replace />;

    const submit = async (e) => {
        e?.preventDefault?.();
        setError("");
        const payload = { login_token: loginToken };
        if (mode === "totp") {
            const clean = (code || "").replace(/\s+/g, "");
            if (!/^\d{6}$/.test(clean)) {
                setError(t.totpInvalidFormat);
                return;
            }
            payload.code = clean;
        } else {
            const clean = (backupCode || "").trim().toUpperCase();
            if (!clean) {
                setError(t.totpBackupRequired);
                return;
            }
            payload.backup_code = clean;
        }
        setLoading(true);
        const res = await loginTotp(payload);
        setLoading(false);
        if (!res.ok) {
            setError(res.error);
            toast.error(res.error);
            return;
        }
        if (res.used_backup_code) {
            toast.warning(t.totpBackupUsedWarning);
        } else {
            toast.success(t.loginSuccess);
        }
        navigate("/", { replace: true });
    };

    const iconSide = lang === "ar" ? { right: "0.75rem" } : { left: "0.75rem" };
    const inputPad = lang === "ar" ? "h-11 pe-10 ps-3" : "h-11 ps-10 pe-3";

    return (
        <div className="relative min-h-screen w-full app-bg" dir={t.dir}>
            <LangToggle lang={lang} onToggle={toggle} t={t} />
            <div className="mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-2">
                <div className="relative hidden p-6 lg:block">
                    <BrandPanel lang={lang} strings={t} />
                </div>
                <div className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 lg:px-12">
                    <div className="w-full max-w-md fade-up">
                        <div className="mb-6 flex items-center gap-3">
                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-700 text-white">
                                <ShieldCheck className="h-5 w-5" />
                            </div>
                            <div>
                                <h1 className="font-heading text-xl font-bold sm:text-2xl">{t.totpChallengeTitle}</h1>
                                <p className="text-xs text-neutral-500 sm:text-sm">{companyName}</p>
                            </div>
                        </div>
                        <p className="mt-2 text-sm text-neutral-600">
                            {mode === "totp" ? t.totpChallengeSubtitle : t.totpBackupSubtitle}
                        </p>

                        <form onSubmit={submit} className="mt-6 space-y-4" data-testid="totp-challenge-form">
                            {mode === "totp" ? (
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.totpCodeLabel}</Label>
                                    <div className="relative">
                                        <Smartphone className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            value={code}
                                            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                            className={`${inputPad} font-numbers tracking-[0.4em] text-center text-lg`}
                                            placeholder="••••••"
                                            inputMode="numeric"
                                            autoFocus
                                            data-testid="totp-code-input"
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-neutral-500">{t.totpCodeHint}</p>
                                </div>
                            ) : (
                                <div>
                                    <Label className="mb-1.5 block text-sm font-medium">{t.totpBackupLabel}</Label>
                                    <div className="relative">
                                        <Key className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" style={iconSide} />
                                        <Input
                                            value={backupCode}
                                            onChange={(e) => setBackupCode(e.target.value.toUpperCase().slice(0, 16))}
                                            className={`${inputPad} font-numbers tracking-widest text-center`}
                                            placeholder="XXXX-XXXX"
                                            autoFocus
                                            data-testid="totp-backup-input"
                                        />
                                    </div>
                                    <p className="mt-1.5 text-xs text-neutral-500">{t.totpBackupHint}</p>
                                </div>
                            )}

                            {error ? (
                                <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="totp-error">
                                    {error}
                                </div>
                            ) : null}

                            <Button
                                type="submit"
                                disabled={loading}
                                className="h-11 w-full bg-emerald-700 text-white hover:bg-emerald-800"
                                data-testid="totp-submit-btn"
                            >
                                {loading ? <Loader2 className="me-2 h-4 w-4 animate-spin" /> : null}
                                {t.totpVerifyAndLogin}
                            </Button>
                        </form>

                        <div className="mt-4 flex flex-col gap-2 text-sm">
                            <button
                                type="button"
                                onClick={() => { setMode(mode === "totp" ? "backup" : "totp"); setError(""); }}
                                className="text-center font-semibold text-amber-700 hover:underline"
                                data-testid="totp-toggle-mode-btn"
                            >
                                {mode === "totp" ? t.totpUseBackupBtn : t.totpUseCodeBtn}
                            </button>
                            <button
                                type="button"
                                onClick={() => navigate("/login", { replace: true })}
                                className="inline-flex items-center justify-center gap-1 text-center text-neutral-500 hover:text-neutral-700"
                                data-testid="totp-back-btn"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                {t.totpBackToLogin}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
